import * as React from 'react'
import { Workflow as FlowArrowIcon } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  useGetPlantProcessDetailQuery,
  useGetPlantProcessTreeQuery
} from '@axilon-apps/domains-ontology-ui-state'
import { Empty } from '../../components/primitives/Empty'
import { plantProcessesIndexHref } from '../../lib/plant-links'
import { ProcessTreeNav } from './ProcessTreeNav'
import { ProcessDetailPanel } from './ProcessDetailPanel'

/**
 * `/plant/processes` — Processes tab.
 *
 * Single page for both unselected (empty pane on the right) and selected
 * (`?process=<id>`) states. Keeping it under one component means the
 * left-rail tree stays mounted across selection changes, so clicking
 * around in the tree no longer feels like a page reload. Tree
 * expand/collapse state, scroll position, and other non-URL UI state
 * survive intra-tab navigation.
 *
 * URL contract: the selected process lives in the `?process=` query
 * param (see `plantHref` in the shared Plant links package).
 */
const ProcessesPage = (): React.ReactElement => {
  const [searchParams] = useSearchParams()
  const rawProcessParam = searchParams.get('process')
  // URN-style ids (e.g. `urn:proc:ASSEMBLY`) round-trip with
  // plantHref encodes URN-style ids; decode here so the query
  // hook sees the raw id.
  const selectedProcessId = rawProcessParam ? decodeURIComponent(rawProcessParam) : null

  const tree = useGetPlantProcessTreeQuery()
  const nodes = tree.data?.nodes ?? []

  const detail = useGetPlantProcessDetailQuery(selectedProcessId ?? '', {
    skip: !selectedProcessId
  })

  const breadcrumbName = detail.data?.process.name ?? selectedProcessId ?? ''

  return (
    <div className="flex flex-1 flex-col gap-card">
      {selectedProcessId ? (
        <nav className="text-sm text-ink-3" aria-label="Breadcrumb">
          <Link to={plantProcessesIndexHref()} className="hover:text-ink hover:underline">
            Processes
          </Link>
          <span className="mx-1.5 text-ink-4">/</span>
          <span className="text-ink">{breadcrumbName}</span>
        </nav>
      ) : (
        <header data-tour-id="plant-processes-header">
          <h1 className="m-0 text-2xl font-semibold text-ink">Processes</h1>
          <p className="mt-1 text-sm text-ink-3">
            {processSubtitle(nodes.length, totalImplementations(nodes))}
          </p>
        </header>
      )}

      <div
        data-tour-id="plant-processes-surface"
        className="grid grid-cols-1 gap-4 lg:grid-cols-[17.5rem_minmax(0,1fr)]"
      >
        <ProcessTreeNav
          nodes={nodes}
          isLoading={tree.isLoading}
          selectedId={selectedProcessId ?? undefined}
        />
        <div className="flex min-w-0 flex-col gap-card">
          {selectedProcessId ? (
            <ProcessDetailPanel processId={selectedProcessId} />
          ) : (
            <div className="flex min-h-80 flex-col rounded-xl border bg-card text-card-foreground shadow-sm">
              {tree.isError ? (
                <Empty
                  title="Couldn't load processes"
                  hint="Check the proxy is reachable and try again."
                  icon={FlowArrowIcon}
                />
              ) : (
                <Empty
                  title="Select a process"
                  hint="Pick a process from the tree to see its inputs, outputs, and implementations."
                  icon={FlowArrowIcon}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function totalImplementations(nodes: { implementationCount: number }[]): number {
  let n = 0
  for (const node of nodes) n += node.implementationCount
  return n
}

function processSubtitle(processCount: number, implementationCount: number): string {
  if (processCount === 0) {
    return 'Process tree, inputs and outputs, and where each process is implemented across sites.'
  }
  return `${processCount} ${processCount === 1 ? 'process' : 'processes'} · ${implementationCount} ${implementationCount === 1 ? 'implementation' : 'implementations'} across sites.`
}

export default ProcessesPage
